import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { Toaster } from "sonner";
import { AuthProvider } from "@/context/AuthContext";
import { ErrorBoundary } from "@/components/ErrorBoundary";
import { ProtectedRoute } from "@/components/ProtectedRoute";
import { AdminRoute } from "@/components/AdminRoute";
import { AppLayout } from "@/components/AppLayout";

import Landing from "@/pages/Landing";
import SignIn from "@/pages/SignIn";
import SignUp from "@/pages/SignUp";
import ForgotPassword from "@/pages/ForgotPassword";
import ResetPassword from "@/pages/ResetPassword";
import VerifyEmail from "@/pages/VerifyEmail";
import AuthCallback from "@/pages/AuthCallback";
import AdminLogin from "@/pages/admin/AdminLogin";
import Onboarding from "@/pages/Onboarding";
import HomePage from "@/pages/Home";
import Safety from "@/pages/Safety";
import Care from "@/pages/Care";
import ActivityPage from "@/pages/ActivityPage";
import Profile from "@/pages/Profile";
import TrustedContactsPage from "@/pages/TrustedContactsPage";
import CommunityPage from "@/pages/CommunityPage";
import Permissions from "@/pages/Permissions";
import EmergencySetup from "@/pages/EmergencySetup";
import SosFlow from "@/pages/emergency/SosFlow";
import EmergencyLive from "@/pages/emergency/EmergencyLive";
import Responder from "@/pages/Responder";
import Analytics from "@/pages/Analytics";
import CommunityLive from "@/pages/CommunityLive";
import CommunityHub from "@/pages/community/CommunityHub";
import Trust from "@/pages/Trust";
import Moderation from "@/pages/Moderation";
import GuardianJourney from "@/pages/journey/GuardianJourney";
import JourneyShare from "@/pages/journey/JourneyShare";
import GuardianAssistant from "@/pages/assistant/GuardianAssistant";
import NotFound from "@/pages/NotFound";
import { Terms, Privacy, BetaSafety, CommunityGuidelines } from "@/pages/Legal";

function AppRoutes() {
  const location = useLocation();
  if (location.hash?.includes("session_id=")) return <AuthCallback />;
  return (
    <Routes>
      <Route path="/" element={<Landing />} />
      <Route path="/auth/sign-in" element={<SignIn />} />
      <Route path="/auth/sign-up" element={<SignUp />} />
      <Route path="/auth/forgot-password" element={<ForgotPassword />} />
      <Route path="/auth/reset-password" element={<ResetPassword />} />
      <Route path="/auth/verify-email" element={<VerifyEmail />} />
      <Route path="/admin/login" element={<AdminLogin />} />
      <Route path="/onboarding" element={<ProtectedRoute><Onboarding /></ProtectedRoute>} />

      <Route element={<ProtectedRoute><AppLayout /></ProtectedRoute>}>
        <Route path="/home" element={<HomePage />} />
        <Route path="/safety" element={<Safety />} />
        <Route path="/care" element={<Care />} />
        <Route path="/activity" element={<ActivityPage />} />
        <Route path="/profile" element={<Profile />} />
        <Route path="/profile/trusted-contacts" element={<TrustedContactsPage />} />
        <Route path="/profile/community" element={<CommunityPage />} />
        <Route path="/profile/permissions" element={<Permissions />} />
        <Route path="/profile/trust" element={<Trust />} />
        <Route path="/community" element={<CommunityHub />} />
        <Route path="/community/pulse" element={<CommunityLive />} />
        <Route path="/journey" element={<GuardianJourney />} />
        <Route path="/assistant" element={<GuardianAssistant />} />
        <Route path="/analytics" element={<Analytics />} />
        <Route path="/responder" element={<Responder />} />
      </Route>

      <Route path="/admin" element={<AdminRoute><div className="min-h-screen bg-[#FFF7E6]"><div className="max-w-lg mx-auto pb-12 pt-2 min-h-screen"><Moderation /></div></div></AdminRoute>} />

      <Route path="/emergency/setup" element={<ProtectedRoute><EmergencySetup /></ProtectedRoute>} />
      <Route path="/emergency/sos" element={<ProtectedRoute><SosFlow /></ProtectedRoute>} />
      <Route path="/emergency/:id" element={<ProtectedRoute><EmergencyLive /></ProtectedRoute>} />

      <Route path="/journey/track/:token" element={<JourneyShare />} />

      <Route path="/legal/terms" element={<Terms />} />
      <Route path="/legal/privacy" element={<Privacy />} />
      <Route path="/legal/beta-safety" element={<BetaSafety />} />
      <Route path="/legal/community-guidelines" element={<CommunityGuidelines />} />

      <Route path="/404" element={<NotFound />} />
      <Route path="*" element={<NotFound />} />
    </Routes>
  );
}

function App() {
  return (
    <ErrorBoundary>
      <BrowserRouter>
        <AuthProvider>
          <AppRoutes />
          <Toaster position="top-center" richColors />
        </AuthProvider>
      </BrowserRouter>
    </ErrorBoundary>
  );
}

export default App;
